import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

// Mirrors backend/utils/accountCategories.js RECEIVABLE_CATEGORIES.
const ACCOUNT_CATEGORIES = { cash: 'Cash', bank: 'Bank', mobile_money: 'Mobile Money' };

const PaymentModeManagement = () => {
  const [modes, setModes] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleModes, resultCount, totalCount
  } = useTableSearch(modes, {
    searchFields: ['name', 'accountCategory'],
    filters: {
      accountCategory: (row, v) => row.accountCategory === v
    }
  });
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', accountCategory: '' });

  useEffect(() => {
    fetchModes();
    fetchAccounts();
  }, []);

  const fetchModes = async () => {
    try {
      const res = await axios.get('/payment-modes');
      setModes(res.data || []);
    } catch (error) {
      toast.error('Error fetching payment modes');
    }
  };

  // Only asset accounts can receive money — the server enforces the same rule, since
  // posting a receipt to an income or expense account would land it on the wrong side.
  const fetchAccounts = async () => {
    try {
      const res = await axios.get('/accounting/chart-of-accounts');
      const all = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setAccounts(all.filter((a) => a.type === 'asset' && a.isActive !== false));
    } catch (error) {
      toast.error('Error fetching chart of accounts');
    }
  };

  const handleOpen = (mode = null) => {
    if (mode) {
      setEditing(mode._id);
      setFormData({
        name: mode.name,
        description: mode.description || '',
        accountCategory: mode.accountCategory || '',
      });
    } else {
      setEditing(null);
      setFormData({ name: '', description: '', accountCategory: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({ name: '', description: '', accountCategory: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Payment mode name is required');
      return;
    }
    if (!formData.accountCategory) {
      toast.error('Please choose the kind of account this mode receives into');
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        const res = await axios.put(`/payment-modes/${editing}`, formData);
        const existing = res.data?.existingPayments || 0;
        toast.success(
          existing > 0
            ? `Mode updated. ${existing} existing payment(s) keep the account they were actually received into.`
            : 'Payment mode updated'
        );
      } else {
        await axios.post('/payment-modes', formData);
        toast.success('Payment mode created');
      }
      fetchModes();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving payment mode');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (mode) => {
    if (!window.confirm(`Delete the "${mode.name}" payment mode? If any payments already use it, it will be deactivated instead so those records stay intact.`)) {
      return;
    }
    try {
      const res = await axios.delete(`/payment-modes/${mode._id}`);
      toast.success(res.data?.message || 'Payment mode deleted');
      fetchModes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting payment mode');
    }
  };

  const handleReactivate = async (mode) => {
    try {
      await axios.put(`/payment-modes/${mode._id}`, { isActive: true });
      toast.success('Payment mode reactivated');
      fetchModes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error reactivating payment mode');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>Payment Modes</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Mode
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        These are the ways money reaches you. Each mode names the <em>kind</em> of account
        it lands in — when staff record a payment they pick a mode and are then offered the
        accounts of that kind from the Chart of Accounts. So one "Bank Transfer" mode covers
        every bank account you hold.
      </Alert>

      {/* A mode is only usable once at least one account of its kind exists, so name the
          gaps here rather than leaving staff to find an empty dropdown mid-payment. */}
      {Object.entries(ACCOUNT_CATEGORIES)
        .filter(([key]) => modes.some((m) => m.accountCategory === key))
        .filter(([key]) => !accounts.some((a) => a.accountCategory === key))
        .map(([key, label]) => (
          <Alert severity="warning" sx={{ mb: 2 }} key={key}>
            No account is classified as <strong>{label}</strong>, so modes using it cannot be
            selected on a payment. Add one under Accounting → Chart of Accounts and set its
            Money Type to {label}.
          </Alert>
        ))}

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Mode name"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
          { key: 'accountCategory', label: 'Category', value: filterValues.accountCategory || '',
            onChange: (v) => setFilter('accountCategory', v),
            options: [
              { value: 'cash', label: 'Cash' },
              { value: 'bank', label: 'Bank' },
              { value: 'mobile_money', label: 'Mobile Money' },
              { value: 'other', label: 'Other' }
            ] }
        ]}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Mode</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Receives Into</TableCell>
              <TableCell>Accounts Available</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleModes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No payment modes yet. Click "Add Mode" to create one.
                </TableCell>
              </TableRow>
            ) : (
              visibleModes.map((mode) => (
                <TableRow key={mode._id}>
                  <TableCell sx={{ fontWeight: 500 }}>{mode.name}</TableCell>
                  <TableCell>{mode.description || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${ACCOUNT_CATEGORIES[mode.accountCategory] || mode.accountCategory} accounts`}
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const matching = accounts.filter((a) => a.accountCategory === mode.accountCategory);
                      if (matching.length === 0) {
                        return <Typography variant="body2" color="error">None — mode unusable</Typography>;
                      }
                      return (
                        <Typography variant="body2">
                          {matching.map((a) => `${a.code} ${a.name}`).join(', ')}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={mode.isActive ? 'Active' : 'Inactive'}
                      color={mode.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpen(mode)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {mode.isActive ? (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(mode)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reactivate">
                        <IconButton size="small" onClick={() => handleReactivate(mode)} color="success">
                          <UndoIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Payment Mode' : 'Add Payment Mode'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Mode Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              helperText="e.g. Bank Transfer, Cheque, Mobile Money, Cash"
            />
            <TextField
              fullWidth
              select
              label="Receives Into"
              value={formData.accountCategory}
              onChange={(e) => setFormData({ ...formData, accountCategory: e.target.value })}
              margin="normal"
              required
              helperText="Staff choosing this mode will be offered the accounts of this kind"
            >
              {Object.entries(ACCOUNT_CATEGORIES).map(([key, label]) => {
                const count = accounts.filter((a) => a.accountCategory === key).length;
                return (
                  <MenuItem key={key} value={key}>
                    {label} {count === 0 ? '(no accounts yet)' : `(${count} account${count === 1 ? '' : 's'})`}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Optional"
            />
            {editing && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Changing this affects payments taken from now on. Past payments keep the
                account they were actually received into — a ledger is not rewritten because
                a setting changed.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name.trim() || !formData.accountCategory}
          >
            {loading ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PaymentModeManagement;
